import React from 'react';
import PropTypes from 'prop-types';
import { arc } from 'd3-shape';
import { scaleLinear } from 'd3-scale';

import style from './gauge.scss';

const getCoordsOnArc = (angle, offset = 10) => [
    Math.cos(angle - (Math.PI / 2)) * offset,
    Math.sin(angle - (Math.PI / 2)) * offset
];

const convertRangesToPercentages = levelRanges => {
    const lowest = levelRanges[0].min;
    const highest = levelRanges[levelRanges.length - 1].max;

    const spread = 1 / (highest - lowest);

    const toReturn = [];

    levelRanges.forEach(range => {
        toReturn.push({
            min: (range.min - lowest) * spread,
            max: (range.max - lowest) * spread
        });
    });

    return toReturn;
};

/**
 * Gauge chart score bar with optional range and color
 *
 * @example
 * <Gauge
 *     classes={{
 *         root: classes.root
 *         colors: classes.colors
 *     }}
 *     scoreMin={ scoreMin }
 *     scoreMax={ scoreMax }
 *     scoreValue={ scoreValue }
 *     initials={ initials }
 *     levelRanges={ levelRanges }
 * />
 */

export const Gauge = ({
    scoreValue,
    scoreMin = 1,
    scoreMax = 7,
    initials,
    levelRanges,
    classes
}) => {

    if (!levelRanges) {
        return null;
    }

    const rangePercentages = convertRangesToPercentages(levelRanges);

    const percentScale = scaleLinear()
        .domain([scoreMin, scoreMax])
        .range([0, 1]);
    const percent = percentScale(scoreValue);

    const angleScale = scaleLinear()
        .domain([0, 1])
        .range([-Math.PI / 2, Math.PI / 2])
        .clamp(true);

    const angle = angleScale(percent);

    const markerLocation = getCoordsOnArc(
        angle,
        1 - ((1 - 0.90) / 2),
    );

    const svgElements = [];

    levelRanges.forEach((range, i) => {
        const levelArc = arc()
            .innerRadius(1)
            .outerRadius(0.92)
            .startAngle(angleScale(rangePercentages[i].min))
            .endAngle(angleScale(rangePercentages[i].max))
            .padAngle(0.01)
            .cornerRadius(0.02)();

        svgElements.push(<path key={i} d={ levelArc } className={classes.colors[range.color]} />);
    });

    const getBlobColor = value => {
        const lastRange = levelRanges[levelRanges.length - 1];

        for (const range of levelRanges.values()) {
            if (value >= range.min && value < range.max) {
                return classes.colors[range.color];
            }
            if (value === lastRange.max) {
                return classes.colors[lastRange.color];
            }
        }

        return '';
    };

    return (
        <svg viewBox={[-1, -1, 2, 1].join(' ')} className={ `${style.gauge} ${classes.root}` }>
            {levelRanges &&
            <>
                { svgElements }
                <circle
                    cx={markerLocation[0]}
                    cy={markerLocation[1]}
                    r="0.14"
                    className={getBlobColor(scoreValue)}
                />
                <text x={markerLocation[0] - 0.07} y={markerLocation[1] + 0.04} fontSize="0.13" fill="white">{initials}</text>
            </>
            }
        </svg>
    );
};

Gauge.propTypes = {
    scoreValue: PropTypes.number.isRequired,
    scoreMin: PropTypes.number,
    scoreMax: PropTypes.number,
    initials: PropTypes.string.isRequired,
    levelRanges: PropTypes.array.isRequired,
    classes: PropTypes.shape({
        root: PropTypes.string,
        colors: PropTypes.object.isRequired
    })
};
